import { type CSSProperties, type FC, useState } from 'react';

import { VIDEO_FORMATS } from '@cofenster/constants';
import { GridContainer, GridItem, NativeVideoPlayer, styled, Typography } from '@cofenster/web-components';

import type { SharingLink } from '../../server/sharingLink/repository/SharingLinkRepository';
import { Captions } from '../components/Captions';
import { PageWrapper } from '../components/PageWrapper';
import { VideoDescription } from '../components/VideoDescription';
import { useTrackWatchTime } from '../hooks/useTrackWatchTime';
import { EmbeddedVideoPage } from './EmbeddedVideoPage';
import { TrackView } from './TrackView';

// 1. Prevents overlapping lines
const StyledVideo = styled(NativeVideoPlayer)(({ theme }) => ({
  display: 'block',
  width: 'auto',
  maxWidth: '100%',
  maxHeight: 'min(calc(100vh - 240px), var(--video-height))',
  aspectRatio: 'var(--aspect-ratio)',

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    maxHeight: 'calc(100vh - 100px)',
  },

  '::cue': {
    whiteSpace: 'pre-line',
    overflowWrap: 'break-word',
    lineHeight: 1.35, // 1
  },
}));

export type VideoPageProps = {
  embedded?: boolean;
  videoId?: string;
  sharingLink: SharingLink;
  defaultCaptionLanguage?: string;
};

export const VideoPage: FC<VideoPageProps> = ({ embedded = false, sharingLink, defaultCaptionLanguage }) => {
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);

  useTrackWatchTime(sharingLink.projectId, video);

  if (embedded) {
    return <EmbeddedVideoPage sharingLink={sharingLink} defaultCaptionLanguage={defaultCaptionLanguage} />;
  }

  const { aspectRatio, height } = VIDEO_FORMATS[sharingLink.videoFormat];

  return (
    <PageWrapper>
      <GridContainer spacing={2}>
        <GridItem xs={12} mt={3} display="flex" justifyContent="center">
          <StyledVideo
            src={sharingLink.videoUrl}
            ref={setVideo}
            controls
            actions={['FULLSCREEN', 'PICTURE_IN_PICTURE', 'PLAYBACK_RATE']}
            objectFit="contain"
            data-testid="video-player"
            style={
              {
                '--aspect-ratio': String(aspectRatio),
                '--video-height': `${height}px`,
              } as CSSProperties
            }
          >
            <Captions sharingLink={sharingLink} defaultLanguage={defaultCaptionLanguage} />
          </StyledVideo>
          <TrackView video={video} videoId={sharingLink.projectId} />
        </GridItem>

        <GridItem xs={12}>
          <Typography component="h1" variant="h3" data-testid="shared-video-title">
            {sharingLink.name}
          </Typography>
        </GridItem>

        <GridItem xs={12} display="flex">
          <VideoDescription title={sharingLink.companyName} createdAt={sharingLink.createdAt} />
        </GridItem>
      </GridContainer>
    </PageWrapper>
  );
};
// displayName is required by `ssrMiddleware::generateRenderWithHydration` function
VideoPage.displayName = 'VideoPage';
